/**
 * Учебная электронная таблица для ЕГЭ 3 / 18.
 * Live-черновик формулы через awareness; режим указания ячеек как в Calc.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import api from '../../api/client';
import { useWorkspaceSync } from './useWorkspaceSync';
import {
  colLabel,
  displayValue,
  makeEvaluator,
  rawCell,
} from './spreadsheet/formulaEngine.js';
import {
  DEFAULT_COLS_18,
  DEFAULT_ROWS_18,
  FILL_PRESETS,
  MAX_FULL_COLS,
  applyAlignToSelection,
  applyFillToSelection,
  applyWallsToSelection,
  clearWallsInSelection,
  copyFormat,
  deleteCols,
  deleteRows,
  emptySpreadsheetState,
  fillAntAcrossRanges,
  fillFormulas,
  findAutoFillDownRow,
  getAlign,
  getColWidth,
  getFill,
  getWalls,
  insertCols,
  insertRows,
  normalizeSpreadsheetState,
  selectionBounds,
  setCellValue,
  setColWidths,
  shouldVirtualize,
} from './spreadsheet/cellModel.js';
import {
  buildSheetAwarenessPayload,
  clearLocalSheetAwareness,
  listRemoteSheetStates,
  remoteEditorOnCell,
  remoteRangesOnCell,
  setLocalSheetAwareness,
} from './spreadsheet/awarenessDraft.js';
import {
  applyPointedColRange,
  applyPointedRange,
  applyPointedRef,
  canPointInsert,
  extractFormulaRefs,
  movePoint,
} from './spreadsheet/formulaPointing.js';
import {
  clearSelectionCells,
  clipboardFromTsv,
  isSingleCellClipboard,
  normalizeTsvText,
  pasteClipboard,
  pasteSingleAcrossRanges,
  selectionToClipboard,
} from './spreadsheet/clipboard.js';
import {
  addRange,
  forEachCellInRanges,
  isInRanges,
  primaryRange,
  replaceRanges,
  serializeRanges,
  setLastRangeFromAnchor,
  startRangeAt,
} from './spreadsheet/selectionModel.js';
import {
  FILTER_OPS,
  clearColumnFilter,
  computeVisibleRows,
  normalizeFilterState,
  setColumnFilter,
  setFilterEnabled,
  uniqueColumnValues,
} from './spreadsheet/filterModel.js';
import { appendAction } from './spreadsheet/actionLog.js';

const ROW_H = 28;
const OVERSCAN = 8;
const AWARENESS_THROTTLE_MS = 60;
const REF_HL_COLORS = [
  '#2563eb',
  '#dc2626',
  '#16a34a',
  '#ca8a04',
  '#9333ea',
  '#0891b2',
];

function isSheetEmpty(state) {
  const cells = state?.cells;
  if (!cells || typeof cells !== 'object') return true;
  return Object.keys(cells).length === 0;
}

function wallClassName(walls) {
  if (!walls) return '';
  return [
    walls.t ? 'ss-wall-t' : '',
    walls.r ? 'ss-wall-r' : '',
    walls.b ? 'ss-wall-b' : '',
    walls.l ? 'ss-wall-l' : '',
  ].filter(Boolean).join(' ');
}

const WALL_SIDE_BTNS = [
  { key: 't', title: 'Верх' },
  { key: 'b', title: 'Низ' },
  { key: 'l', title: 'Лево' },
  { key: 'r', title: 'Право' },
];

function WallSideIcon({ side }) {
  return (
    <span className={`ss-wall-icon ss-wall-icon-${side}`} aria-hidden>
      <span className="ss-wall-icon-box" />
    </span>
  );
}

export default function SpreadsheetWorkspace({
  ydoc,
  awareness = null,
  workspaceConfig,
  seedState,
  dataKey = 'data',
  onStateChange,
  readOnly = false,
  taskSlug = null,
}) {
  const initial = useMemo(() => {
    const cfg = workspaceConfig && typeof workspaceConfig === 'object' ? workspaceConfig : {};
    let rows = Number(cfg.rows) > 0 ? Number(cfg.rows) : DEFAULT_ROWS_18;
    let cols = Number(cfg.cols) > 0 ? Number(cfg.cols) : DEFAULT_COLS_18;
    rows = Math.min(Math.max(1, rows), 20000);
    cols = Math.min(Math.max(1, cols), MAX_FULL_COLS);
    const base = emptySpreadsheetState(rows, cols);
    return normalizeSpreadsheetState(
      {
        ...base,
        ...(cfg.cells ? { cells: cfg.cells } : {}),
        ...(cfg.fills ? { fills: cfg.fills } : {}),
        ...(cfg.walls ? { walls: cfg.walls } : {}),
        rows,
        cols,
      },
      base,
    );
  }, [workspaceConfig]);

  const [state, setState] = useWorkspaceSync({
    ydoc,
    initialState: initial,
    seedState: seedState ? normalizeSpreadsheetState(seedState, initial) : null,
    dataKey,
    onChange: onStateChange,
  });

  const rows = state.rows || DEFAULT_ROWS_18;
  const cols = Math.min(state.cols || DEFAULT_COLS_18, MAX_FULL_COLS);
  const virtual = shouldVirtualize(state, workspaceConfig);
  const evalCell = useMemo(() => makeEvaluator(state), [state]);

  const [ranges, setRanges] = useState(() => startRangeAt(0, 0));
  const [active, setActive] = useState({ row: 0, col: 0 });
  /** @type {[null|{row,col,draft,caret?:number}, Function]} */
  const [editing, setEditing] = useState(null);
  const formulaRefHighlights = useMemo(() => {
    if (!editing || !String(editing.draft || '').startsWith('=')) return [];
    return extractFormulaRefs(editing.draft, { rows });
  }, [editing, rows]);

  const visibleRows = useMemo(
    () => computeVisibleRows(state, state.filter, evalCell),
    [state, evalCell],
  );

  const filterState = normalizeFilterState(state.filter);

  const [formulaBar, setFormulaBar] = useState('');
  /** @type {[null|{refStart,refEnd,row,col}, Function]} */
  const [pointing, setPointing] = useState(null);
  const [paintMode, setPaintMode] = useState(false);
  const [fillColor, setFillColor] = useState(FILL_PRESETS[1].color);
  const [wallSides, setWallSides] = useState({ t: true, r: true, b: true, l: true });
  const [sheetLoading, setSheetLoading] = useState(false);
  const [sheetError, setSheetError] = useState('');
  const [sheetSource, setSheetSource] = useState('');
  const [remotes, setRemotes] = useState([]);
  const [lockHint, setLockHint] = useState('');
  /** @type {[null|{origin,value,fill,bounds}, Function]} */
  const [ants, setAnts] = useState(null);
  const [pressedKeys, setPressedKeys] = useState([]);
  const [filterPopupCol, setFilterPopupCol] = useState(null);
  /** @type {[null|{x,y,kind:'col'|'row', r0,r1,c0,c1}, Function]} */
  const [ctxMenu, setCtxMenu] = useState(null);

  const autoLoadTried = useRef(false);
  const stateRef = useRef(state);
  stateRef.current = state;
  const editingRef = useRef(editing);
  editingRef.current = editing;
  const pointingRef = useRef(pointing);
  pointingRef.current = pointing;
  const activeRef = useRef(active);
  activeRef.current = active;
  const rangesRef = useRef(ranges);
  rangesRef.current = ranges;
  const antsRef = useRef(ants);
  antsRef.current = ants;
  const pressedKeysRef = useRef(pressedKeys);
  pressedKeysRef.current = pressedKeys;
  const skipBlurCommitRef = useRef(false);
  const awarenessTimerRef = useRef(null);
  const dragging = useRef(null);
  const selectAnchorRef = useRef(null);
  const fillOrigin = useRef(null);
  const scrollRef = useRef(null);
  const workspaceRef = useRef(null);
  const editInputRef = useRef(null);
  /** @type {React.MutableRefObject<ReturnType<typeof selectionToClipboard>|null>} */
  const internalClipboardRef = useRef(null);
  /** true = F2/двойной клик (стрелки в тексте); false = набор с клавиши (стрелка коммитит) */
  const editDeepRef = useRef(false);
  const formulaDragRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewH, setViewH] = useState(400);

  const focusWorkspace = useCallback(() => {
    const el = workspaceRef.current;
    if (!el || typeof el.focus !== 'function') return;
    try {
      el.focus({ preventScroll: true });
    } catch {
      el.focus();
    }
  }, []);

  const publishAwareness = useCallback((nextEditing, nextPointing, nextActive, nextRanges) => {
    if (!awareness) return;
    const ed = nextEditing === undefined ? editingRef.current : nextEditing;
    const pt = nextPointing === undefined ? pointingRef.current : nextPointing;
    const act = nextActive === undefined ? activeRef.current : nextActive;
    const rng = nextRanges === undefined ? rangesRef.current : nextRanges;
    const antsBounds = antsRef.current?.bounds || null;
    let pointPayload = null;
    if (pt) {
      if (pt.r0 != null) {
        pointPayload = {
          row: pt.row ?? pt.r0,
          col: pt.col ?? pt.c0,
          r0: pt.r0,
          r1: pt.r1,
          c0: pt.c0,
          c1: pt.c1,
        };
      } else {
        pointPayload = { row: pt.row, col: pt.col };
      }
    }
    const payload = buildSheetAwarenessPayload({
      dataKey,
      active: act,
      ranges: serializeRanges(rng),
      editing: ed,
      pointTo: pointPayload,
      ants: antsBounds,
      keys: pressedKeysRef.current,
    });
    if (awarenessTimerRef.current) clearTimeout(awarenessTimerRef.current);
    awarenessTimerRef.current = setTimeout(() => {
      setLocalSheetAwareness(awareness, payload);
    }, AWARENESS_THROTTLE_MS);
  }, [awareness, dataKey]);

  const flushAwarenessNow = useCallback((nextEditing, nextPointing) => {
    if (!awareness) return;
    if (awarenessTimerRef.current) {
      clearTimeout(awarenessTimerRef.current);
      awarenessTimerRef.current = null;
    }
    const ed = nextEditing === undefined ? editingRef.current : nextEditing;
    const pt = nextPointing === undefined ? pointingRef.current : nextPointing;
    const antsBounds = antsRef.current?.bounds || null;
    setLocalSheetAwareness(
      awareness,
      buildSheetAwarenessPayload({
        dataKey,
        active: activeRef.current,
        ranges: serializeRanges(rangesRef.current),
        editing: ed,
        pointTo: pt ? { row: pt.row, col: pt.col } : null,
        ants: antsBounds,
        keys: pressedKeysRef.current,
      }),
    );
  }, [awareness, dataKey]);

  useEffect(() => {
    if (!awareness) {
      setRemotes([]);
      return undefined;
    }
    const sync = () => setRemotes(listRemoteSheetStates(awareness, dataKey));
    sync();
    awareness.on('change', sync);
    return () => {
      awareness.off('change', sync);
      clearLocalSheetAwareness(awareness);
      if (awarenessTimerRef.current) clearTimeout(awarenessTimerRef.current);
    };
  }, [awareness, dataKey]);

  // Навигация / муравьи / клавиши — в awareness
  useEffect(() => {
    if (!awareness || editing) return;
    publishAwareness(null, null, active, ranges);
  }, [awareness, active, ranges, editing, ants, pressedKeys, publishAwareness]);

  // Зажатые клавиши → эфир (и локальный оверлей)
  useEffect(() => {
    const norm = (e) => {
      const parts = [];
      if (e.ctrlKey || e.metaKey) parts.push(e.metaKey ? 'Cmd' : 'Ctrl');
      if (e.altKey) parts.push('Alt');
      if (e.shiftKey) parts.push('Shift');
      const k = e.key;
      if (k && k !== 'Control' && k !== 'Meta' && k !== 'Alt' && k !== 'Shift') {
        if (k.length === 1) parts.push(k.toUpperCase());
        else if (k.startsWith('Arrow')) parts.push(k.replace('Arrow', ''));
        else if (k === 'Enter' || k === 'Escape' || k === 'Tab' || k === 'Backspace'
          || k === 'Delete' || k === 'F2' || /^F\d+$/.test(k)) {
          parts.push(k === 'Escape' ? 'Esc' : k);
        }
      }
      return parts;
    };
    const onDown = (e) => {
      const next = norm(e);
      setPressedKeys(next);
      pressedKeysRef.current = next;
    };
    const onUp = (e) => {
      const next = norm(e);
      // keyup: убрать отпущенную, оставить модификаторы если ещё зажаты
      setPressedKeys(next);
      pressedKeysRef.current = next;
    };
    const onBlur = () => {
      setPressedKeys([]);
      pressedKeysRef.current = [];
    };
    window.addEventListener('keydown', onDown, true);
    window.addEventListener('keyup', onUp, true);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onDown, true);
      window.removeEventListener('keyup', onUp, true);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  // Уже открытые комнаты 20×20 → запас под вторую таблицу
  useEffect(() => {
    if (readOnly) return;
    if ((state.rows || 0) >= DEFAULT_ROWS_18 && (state.cols || 0) >= DEFAULT_COLS_18) return;
    setState((prev) => {
      const nr = Math.max(prev.rows || 0, DEFAULT_ROWS_18);
      const nc = Math.max(prev.cols || 0, DEFAULT_COLS_18);
      if (nr === prev.rows && nc === prev.cols) return prev;
      return { ...prev, rows: nr, cols: nc };
    });
  }, [state.rows, state.cols, readOnly, setState]);

  const applySheetState = useCallback((raw) => {
    const next = normalizeSpreadsheetState(raw, emptySpreadsheetState(
      Number(raw?.rows) || DEFAULT_ROWS_18,
      Number(raw?.cols) || DEFAULT_COLS_18,
    ));
    setState(next);
    setSheetSource(String(raw?.source || ''));
    setActive({ row: 0, col: 0 });
    setRanges(startRangeAt(0, 0));
    setEditing(null);
    setPointing(null);
    setFormulaBar('');
    flushAwarenessNow(null, null);
  }, [setState, flushAwarenessNow]);

  const loadFromTaskFile = useCallback(async ({ force = false, onlyIfEmpty = false } = {}) => {
    if (!taskSlug || readOnly) return;
    if (onlyIfEmpty && !isSheetEmpty(stateRef.current)) return;
    setSheetLoading(true);
    setSheetError('');
    try {
      const qs = force ? '?force=1' : '';
      const { data } = await api.get(`/tasks/${encodeURIComponent(taskSlug)}/spreadsheet-sheet/${qs}`);
      if (!data || typeof data !== 'object') throw new Error('Пустой ответ сервера');
      applySheetState(data);
    } catch (err) {
      setSheetError(String(err?.response?.data?.detail || err?.message || 'Не удалось загрузить таблицу'));
    } finally {
      setSheetLoading(false);
    }
  }, [taskSlug, readOnly, applySheetState]);

  useEffect(() => {
    autoLoadTried.current = false;
  }, [taskSlug]);

  useEffect(() => {
    if (!taskSlug || readOnly || autoLoadTried.current) return undefined;
    const t = window.setTimeout(() => {
      if (autoLoadTried.current) return;
      autoLoadTried.current = true;
      void loadFromTaskFile({ onlyIfEmpty: true });
    }, 700);
    return () => window.clearTimeout(t);
  }, [taskSlug, readOnly, loadFromTaskFile]);

  useEffect(() => {
    if (!editing) return;
    const raw = rawCell(state, active.row, active.col);
    // во время правки formulaBar ведём из draft
    void raw;
  }, [state, active, editing]);

  useEffect(() => {
    if (!editing) {
      setFormulaBar(rawCell(state, active.row, active.col));
    }
  }, [state, active, editing]);

  useEffect(() => {
    if (editing && editInputRef.current) {
      const el = editInputRef.current;
      el.focus();
      if (editing.caret != null && typeof el.setSelectionRange === 'function') {
        const c = Math.max(0, Math.min(editing.draft.length, editing.caret));
        el.setSelectionRange(c, c);
      } else {
        el.select();
      }
    }
  // только при старте правки новой клетки
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing?.row, editing?.col]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const onScroll = () => setScrollTop(el.scrollTop);
    const ro = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => setViewH(el.clientHeight || 400))
      : null;
    el.addEventListener('scroll', onScroll, { passive: true });
    setViewH(el.clientHeight || 400);
    if (ro) ro.observe(el);
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (ro) ro.disconnect();
    };
  }, [virtual]);

  const cancelEdit = useCallback(() => {
    setEditing(null);
    setPointing(null);
    editDeepRef.current = false;
    formulaDragRef.current = null;
    setFormulaBar(rawCell(stateRef.current, activeRef.current.row, activeRef.current.col));
    flushAwarenessNow(null, null);
  }, [flushAwarenessNow]);

  const commitEdit = useCallback(() => {
    if (!editingRef.current || readOnly) {
      setEditing(null);
      setPointing(null);
      editDeepRef.current = false;
      formulaDragRef.current = null;
      flushAwarenessNow(null, null);
      return;
    }
    const ed = editingRef.current;
    setState((prev) => setCellValue(prev, ed.row, ed.col, ed.draft));
    setEditing(null);
    setPointing(null);
    editDeepRef.current = false;
    formulaDragRef.current = null;
    setFormulaBar(ed.draft);
    flushAwarenessNow(null, null);
  }, [readOnly, setState, flushAwarenessNow]);

  const startEdit = useCallback((r, c, initialDraft, opts = {}) => {
    if (readOnly) return false;
    const remote = remoteEditorOnCell(remotes, r, c);
    if (remote && !opts.force) {
      setLockHint(remote.name
        ? `Сейчас правит ${remote.name}`
        : 'Ячейку сейчас правит другой участник');
      window.setTimeout(() => setLockHint(''), 2500);
      return false;
    }
    const raw = initialDraft !== undefined ? initialDraft : rawCell(stateRef.current, r, c);
    const caret = opts.caret != null ? opts.caret : raw.length;
    if (opts.deep != null) editDeepRef.current = Boolean(opts.deep);
    else if (initialDraft !== undefined) editDeepRef.current = false;
    else editDeepRef.current = true;
    setActive({ row: r, col: c });
    setRanges(replaceRanges({r0: r, r1: r, c0: c, c1: c }));
    const ed = { row: r, col: c, draft: raw, caret };
    setEditing(ed);
    setFormulaBar(raw);
    setPointing(null);
    setAnts(null);
    antsRef.current = null;
    flushAwarenessNow(ed, null);
    return true;
  }, [readOnly, remotes, flushAwarenessNow]);

  const applyPointingResult = useCallback((ed, applied) => {
    setPointing(applied.pointing);
    pointingRef.current = applied.pointing;
    const nextEd = {
      ...ed,
      draft: applied.draft,
      caret: applied.caret,
    };
    setEditing(nextEd);
    editingRef.current = nextEd;
    setFormulaBar(applied.draft);
    flushAwarenessNow(nextEd, applied.pointing);
    requestAnimationFrame(() => {
      const el = editInputRef.current;
      if (el && typeof el.setSelectionRange === 'function') {
        el.focus();
        el.setSelectionRange(applied.caret, applied.caret);
      }
    });
  }, [flushAwarenessNow]);

  const insertPointedCell = useCallback((r, c) => {
    const ed = editingRef.current;
    if (!ed || readOnly) return;
    const caret = ed.caret != null ? ed.caret : ed.draft.length;
    if (!canPointInsert(ed.draft, caret) && !pointingRef.current) return;
    const applied = applyPointedRef(ed.draft, caret, r, c, pointingRef.current);
    applyPointingResult(ed, applied);
  }, [readOnly, applyPointingResult]);

  const insertPointedRangeAt = useCallback((anchorRow, anchorCol, r, c) => {
    const ed = editingRef.current;
    if (!ed || readOnly) return;
    const caret = ed.caret != null ? ed.caret : ed.draft.length;
    if (!canPointInsert(ed.draft, caret) && !pointingRef.current) return;
    const applied = applyPointedRange(
      ed.draft,
      caret,
      anchorRow,
      anchorCol,
      r,
      c,
      pointingRef.current,
    );
    applyPointingResult(ed, applied);
  }, [readOnly, applyPointingResult]);

  const insertPointedColsAt = useCallback((anchorCol, col) => {
    const ed = editingRef.current;
    if (!ed || readOnly) return;
    const caret = ed.caret != null ? ed.caret : ed.draft.length;
    if (!canPointInsert(ed.draft, caret) && !pointingRef.current) return;
    const applied = applyPointedColRange(
      ed.draft,
      caret,
      anchorCol,
      col,
      pointingRef.current,
    );
    applyPointingResult(ed, applied);
  }, [readOnly, applyPointingResult]);

  const selectCell = useCallback((r, c, {
    extend = false,
    add = false,
  } = {}) => {
    if (paintMode && !readOnly) {
      setState((prev) => copyFormat(prev, activeRef.current, {
        r0: r, r1: r, c0: c, c1: c,
      }));
      setPaintMode(false);
      setActive({ row: r, col: c });
      setRanges(startRangeAt(r, c));
      return;
    }
    setActive({ row: r, col: c });
    if (add) {
      setRanges((prev) => addRange(prev, { r0: r, r1: r, c0: c, c1: c }));
      selectAnchorRef.current = { row: r, col: c };
    } else if (extend) {
      const anchor = selectAnchorRef.current || activeRef.current;
      setRanges(replaceRanges({
        r0: anchor.row, c0: anchor.col, r1: r, c1: c,
      }));
    } else {
      selectAnchorRef.current = { row: r, col: c };
      setRanges(startRangeAt(r, c));
    }
  }, [paintMode, readOnly, setState]);

  const onCellMouseDown = (e, r, c) => {
    if (e.button !== 0) return;
    e.preventDefault();

    // Режим указания: drag → диапазон A1:B2, клик → одна ячейка
    const ed = editingRef.current;
    if (ed && String(ed.draft || '').startsWith('=')) {
      const caret = ed.caret != null ? ed.caret : ed.draft.length;
      if (canPointInsert(ed.draft, caret) || pointingRef.current) {
        skipBlurCommitRef.current = true;
        const anchor = { row: r, col: c };
        formulaDragRef.current = anchor;
        insertPointedRangeAt(r, c, r, c);
        const onMove = (ev) => {
          const t = document.elementFromPoint(ev.clientX, ev.clientY);
          const cell = t?.closest?.('[data-ss-r]');
          if (!cell) return;
          const nr = Number(cell.getAttribute('data-ss-r'));
          const nc = Number(cell.getAttribute('data-ss-c'));
          if (!Number.isFinite(nr) || !Number.isFinite(nc)) return;
          insertPointedRangeAt(anchor.row, anchor.col, nr, nc);
        };
        const onUp = () => {
          formulaDragRef.current = null;
          window.removeEventListener('mousemove', onMove);
          window.removeEventListener('mouseup', onUp);
          window.setTimeout(() => { skipBlurCommitRef.current = false; }, 0);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        return;
      }
    }

    if (editingRef.current) commitEdit();
    const ctrl = e.ctrlKey || e.metaKey;
    if (ctrl) selectCell(r, c, { add: true });
    else if (e.shiftKey) selectCell(r, c, { extend: true });
    else selectCell(r, c, {});
    focusWorkspace();

    const anchor = selectAnchorRef.current || { row: r, col: c };
    dragging.current = ctrl ? 'select-add' : 'select';
    const onMove = (ev) => {
      const t = document.elementFromPoint(ev.clientX, ev.clientY);
      const cell = t?.closest?.('[data-ss-r]');
      if (!cell) return;
      const nr = Number(cell.getAttribute('data-ss-r'));
      const nc = Number(cell.getAttribute('data-ss-c'));
      if (!Number.isFinite(nr) || !Number.isFinite(nc)) return;
      setRanges((prev) => setLastRangeFromAnchor(prev, anchor.row, anchor.col, nr, nc));
      setActive({ row: nr, col: nc });
    };
    const onUp = () => {
      dragging.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      focusWorkspace();
      appendAction('select', { ranges: serializeRanges(rangesRef.current) });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const fillDownFromHandle = useCallback(() => {
    if (readOnly) return;
    const bounds = primaryRange(rangesRef.current);
    if (!bounds) return;
    const endRow = findAutoFillDownRow(stateRef.current, bounds);
    if (endRow == null) return;
    const dest = {
      r0: bounds.r0,
      r1: endRow,
      c0: bounds.c0,
      c1: bounds.c1,
    };
    setState((prev) => fillFormulas(prev, {
      row: activeRef.current.row,
      col: activeRef.current.col,
    }, dest));
    setRanges(replaceRanges(dest));
    appendAction('fill-down', dest);
  }, [readOnly, setState]);

  const onFillHandleMouseDown = (e) => {
    if (readOnly) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.detail >= 2) {
      fillDownFromHandle();
      return;
    }
    const bounds = primaryRange(ranges);
    if (!bounds) return;
    dragging.current = 'fill';
    const origin = {
      row: active.row,
      col: active.col,
      sel: { ...bounds },
      dest: { ...bounds },
    };
    fillOrigin.current = origin;
    const onMove = (ev) => {
      const t = document.elementFromPoint(ev.clientX, ev.clientY);
      const cell = t?.closest?.('[data-ss-r]');
      if (!cell) return;
      const nr = Number(cell.getAttribute('data-ss-r'));
      const nc = Number(cell.getAttribute('data-ss-c'));
      if (!Number.isFinite(nr) || !Number.isFinite(nc)) return;
      const next = {
        r0: origin.sel.r0, c0: origin.sel.c0, r1: nr, c1: nc,
      };
      origin.dest = next;
      setRanges(replaceRanges(next));
    };
    const onUp = () => {
      dragging.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      const src = fillOrigin.current;
      fillOrigin.current = null;
      if (!src?.dest) return;
      const dest = selectionBounds(src.dest);
      if (!dest) return;
      setState((prev) => fillFormulas(prev, { row: src.row, col: src.col }, dest));
      setRanges(replaceRanges(src.dest));
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const onEditBlur = () => {
    if (skipBlurCommitRef.current) {
      skipBlurCommitRef.current = false;
      return;
    }
    commitEdit();
  };

  const pasteAtActive = useCallback((textFromEvent = null) => {
    if (readOnly) return false;
    const text = textFromEvent;
    const internal = internalClipboardRef.current;
    const ants = antsRef.current;
    const textNorm = text == null ? null : normalizeTsvText(text);
    let payload = null;

    // Свой буфер / муравьи важнее системного текста: браузер часто меняет \\n,
    // и без origin формулы вставлялись бы один-в-один без сдвига ссылок.
    if (internal && (textNorm == null || textNorm === '' || textNorm === normalizeTsvText(internal.tsv))) {
      payload = internal;
    } else if (ants && (textNorm == null || textNorm === '' || textNorm === normalizeTsvText(ants.value))) {
      payload = {
        tsv: String(ants.value ?? ''),
        grid: [[ants.value ?? '']],
        fills: [[ants.fill ?? null]],
        walls: null,
        origin: ants.origin || { row: ants.bounds?.r0 ?? 0, col: ants.bounds?.c0 ?? 0 },
        rows: 1,
        cols: 1,
      };
    } else if (text) {
      payload = clipboardFromTsv(text);
    } else if (internal) {
      payload = internal;
    } else if (ants) {
      payload = {
        tsv: String(ants.value ?? ''),
        grid: [[ants.value ?? '']],
        fills: [[ants.fill ?? null]],
        walls: null,
        origin: ants.origin || { row: ants.bounds?.r0 ?? 0, col: ants.bounds?.c0 ?? 0 },
        rows: 1,
        cols: 1,
      };
    }
    if (!payload) return false;

    const ranges = rangesRef.current?.length
      ? rangesRef.current
      : replaceRanges({
        r0: activeRef.current.row,
        r1: activeRef.current.row,
        c0: activeRef.current.col,
        c1: activeRef.current.col,
      });

    let destCount = 0;
    forEachCellInRanges(ranges, () => { destCount += 1; });

    // Одна скопированная клетка → во всё выделение (Ctrl+клик / диапазон), со сдвигом
    if (isSingleCellClipboard(payload) && destCount > 1) {
      const origin = payload.origin
        || ants?.origin
        || (ants?.bounds ? { row: ants.bounds.r0, col: ants.bounds.c0 } : null);
      const next = pasteSingleAcrossRanges(
        stateRef.current,
        payload,
        ranges,
        { origin },
      );
      setState(next);
      const a = activeRef.current;
      setFormulaBar(rawCell(next, a.row, a.col));
      appendAction('paste-fill', {
        origin,
        ranges: serializeRanges(ranges),
        value: payload.grid?.[0]?.[0],
      });
      return true;
    }

    const primary = primaryRange(ranges);
    const b = primary ? {
      r0: Math.min(primary.r0, primary.r1),
      c0: Math.min(primary.c0, primary.c1),
    } : null;
    const start = b
      ? { row: b.r0, col: b.c0 }
      : activeRef.current;
    const next = pasteClipboard(stateRef.current, start.row, start.col, payload);
    setState(next);
    const h = payload.rows || 1;
    const w = payload.cols || 1;
    setRanges(replaceRanges({
      r0: start.row,
      r1: start.row + h - 1,
      c0: start.col,
      c1: start.col + w - 1,
    }));
    setFormulaBar(rawCell(next, start.row, start.col));
    return true;
  }, [readOnly, setState]);

  const onKeyDown = (e) => {
    const arrowMap = {
      ArrowUp: [-1, 0],
      ArrowDown: [1, 0],
      ArrowLeft: [0, -1],
      ArrowRight: [0, 1],
    };

    if (editingRef.current) {
      const ed = editingRef.current;
      const caret = (editInputRef.current && typeof editInputRef.current.selectionStart === 'number')
        ? editInputRef.current.selectionStart
        : (ed.caret ?? ed.draft.length);

      if (e.key === 'Enter') {
        e.preventDefault();
        commitEdit();
        const nr = Math.min(rows - 1, ed.row + 1);
        setActive({ row: nr, col: ed.col });
        setRanges(replaceRanges({r0: nr, r1: nr, c0: ed.col, c1: ed.col }));
        focusWorkspace();
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        cancelEdit();
        focusWorkspace();
        return;
      }
      if (e.key === 'Tab') {
        e.preventDefault();
        commitEdit();
        const nc = Math.min(cols - 1, ed.col + (e.shiftKey ? -1 : 1));
        setActive({ row: ed.row, col: Math.max(0, nc) });
        setRanges(replaceRanges({r0: ed.row, r1: ed.row, c0: Math.max(0, nc), c1: Math.max(0, nc),
        }));
        focusWorkspace();
        return;
      }

      // Указание ячейки стрелками в формуле
      if (arrowMap[e.key] && String(ed.draft).startsWith('=')) {
        if (canPointInsert(ed.draft, caret) || pointingRef.current) {
          e.preventDefault();
          e.stopPropagation();
          const [dr, dc] = arrowMap[e.key];
          let baseR;
          let baseC;
          if (pointingRef.current) {
            baseR = pointingRef.current.row;
            baseC = pointingRef.current.col;
          } else {
            baseR = ed.row;
            baseC = ed.col;
          }
          const next = movePoint(baseR, baseC, dr, dc, rows, cols);
          // Shift при pointing — расширить диапазон от якоря
          if (e.shiftKey && pointingRef.current?.anchorRow != null) {
            insertPointedRangeAt(
              pointingRef.current.anchorRow,
              pointingRef.current.anchorCol,
              next.row,
              next.col,
            );
          } else {
            const applied = applyPointedRef(
              ed.draft,
              caret,
              next.row,
              next.col,
              pointingRef.current,
            );
            // зафиксировать якорь при первом указании
            if (applied.pointing && applied.pointing.anchorRow == null) {
              applied.pointing.anchorRow = next.row;
              applied.pointing.anchorCol = next.col;
            }
            applyPointingResult(ed, applied);
          }
          return;
        }
      }

      // «Лёгкий» edit (набор с клавиши): стрелка → записать и перейти
      if (arrowMap[e.key] && !editDeepRef.current && !String(ed.draft).startsWith('=')) {
        e.preventDefault();
        const [dr, dc] = arrowMap[e.key];
        commitEdit();
        const nr = Math.max(0, Math.min(rows - 1, ed.row + dr));
        const nc = Math.max(0, Math.min(cols - 1, ed.col + dc));
        setActive({ row: nr, col: nc });
        setRanges(replaceRanges({r0: nr, r1: nr, c0: nc, c1: nc }));
        focusWorkspace();
        return;
      }

      // обычный ввод — сброс pointing, если печатаем не стрелкой
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
        if (pointingRef.current) {
          setPointing(null);
          pointingRef.current = null;
        }
      }
      return;
    }

    if ((e.ctrlKey || e.metaKey) && !e.altKey && e.key.toLowerCase() === 'a') {
      e.preventDefault();
      setRanges(replaceRanges({r0: 0, r1: rows - 1, c0: 0, c1: cols - 1 }));
      return;
    }

    if (e.key === 'F2' || (e.key === 'Enter' && !antsRef.current)) {
      e.preventDefault();
      startEdit(active.row, active.col, undefined, { deep: true });
      return;
    }
    if (e.key === 'Delete' || e.key === 'Backspace') {
      if (readOnly) return;
      e.preventDefault();
      setState((prev) => {
        let next = prev;
        forEachCellInRanges(rangesRef.current, (r, c) => {
          next = setCellValue(next, r, c, '');
        });
        return next;
      });
      setFormulaBar('');
      return;
    }

    const move = (dr, dc, extend) => {
      e.preventDefault();
      const nr = Math.max(0, Math.min(rows - 1, active.row + dr));
      const nc = Math.max(0, Math.min(cols - 1, active.col + dc));
      selectCell(nr, nc, { extend });
    };

    // Enter / Ctrl+Enter при «муравьях» — размножить формулу+цвет
    if (e.key === 'Enter' && antsRef.current && !readOnly) {
      e.preventDefault();
      const a = antsRef.current;
      setState((prev) => fillAntAcrossRanges(prev, a, rangesRef.current));
      appendAction('ants-fill', { ants: a.bounds, ranges: serializeRanges(rangesRef.current) });
      if (!e.ctrlKey && !e.metaKey) {
        setAnts(null);
        antsRef.current = null;
      }
      focusWorkspace();
      return;
    }

    if (e.key === 'Escape' && antsRef.current) {
      e.preventDefault();
      setAnts(null);
      antsRef.current = null;
      return;
    }

    if (e.key === 'ArrowUp') move(-1, 0, e.shiftKey);
    else if (e.key === 'ArrowDown') move(1, 0, e.shiftKey);
    else if (e.key === 'ArrowLeft') move(0, -1, e.shiftKey);
    else if (e.key === 'ArrowRight') move(0, 1, e.shiftKey);
    else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault();
      startEdit(active.row, active.col, e.key, { caret: 1, deep: false });
    }
  };

  const onEditInputChange = (e) => {
    const draft = e.target.value;
    const caret = e.target.selectionStart ?? draft.length;
    if (pointingRef.current) {
      setPointing(null);
      pointingRef.current = null;
    }
    const nextEd = editingRef.current
      ? { ...editingRef.current, draft, caret }
      : null;
    setEditing(nextEd);
    editingRef.current = nextEd;
    setFormulaBar(draft);
    publishAwareness(nextEd, null);
  };

  const onEditSelect = (e) => {
    if (!editingRef.current) return;
    const caret = e.target.selectionStart ?? editingRef.current.draft.length;
    const nextEd = { ...editingRef.current, caret };
    setEditing(nextEd);
    editingRef.current = nextEd;
    publishAwareness(nextEd, pointingRef.current);
  };

  const b = primaryRange(ranges);
  const applyToAllRanges = (fn) => {
    setState((prev) => {
      let next = prev;
      for (const rng of rangesRef.current) {
        next = fn(next, primaryRange([rng]) || rng);
      }
      return next;
    });
  };

  let rowStart = 0;
  let rowEnd = rows - 1;
  let topPad = 0;
  let bottomPad = 0;
  if (virtual) {
    const first = Math.max(0, Math.floor(scrollTop / ROW_H) - OVERSCAN);
    const visible = Math.ceil(viewH / ROW_H) + OVERSCAN * 2;
    rowStart = first;
    rowEnd = Math.min(rows - 1, first + visible);
    topPad = rowStart * ROW_H;
    bottomPad = Math.max(0, (rows - 1 - rowEnd) * ROW_H);
  }

  // Удалённые указатели / черновики
  const remotePointHighlights = [];
  remotes.forEach((r) => {
    const pt = r.sheet?.editing?.pointTo;
    if (pt) remotePointHighlights.push({ ...pt, color: r.color, clientId: r.clientId });
    const act = r.sheet?.active;
    if (act && !r.sheet?.editing) {
      remotePointHighlights.push({
        row: act.row, col: act.col, color: r.color, clientId: r.clientId, soft: true,
      });
    }
  });

  const localPoint = pointing;

  const refHlOnCell = (r, c) => {
    for (let i = formulaRefHighlights.length - 1; i >= 0; i -= 1) {
      const hl = formulaRefHighlights[i];
      if (r >= hl.r0 && r <= hl.r1 && c >= hl.c0 && c <= hl.c1) return hl;
    }
    return null;
  };

  const selectColumn = (c, extend) => {
    if (extend) {
      const prev = primaryRange(rangesRef.current) || { c0: c, c1: c };
      setRanges(replaceRanges({
        r0: 0,
        r1: rows - 1,
        c0: prev.c0,
        c1: c,
      }));
    } else {
      setRanges(replaceRanges({ r0: 0, r1: rows - 1, c0: c, c1: c }));
    }
    setActive({ row: active.row, col: c });
    focusWorkspace();
  };

  const selectRow = (r, extend) => {
    if (extend) {
      const prev = primaryRange(rangesRef.current) || { r0: r, r1: r };
      setRanges(replaceRanges({
        r0: prev.r0,
        r1: r,
        c0: 0,
        c1: cols - 1,
      }));
    } else {
      setRanges(replaceRanges({ r0: r, r1: r, c0: 0, c1: cols - 1 }));
    }
    setActive({ row: r, col: active.col });
    focusWorkspace();
  };

  const openHeaderContextMenu = (e, kind, index) => {
    e.preventDefault();
    e.stopPropagation();
    if (readOnly) return;
    const prev = primaryRange(rangesRef.current);
    let r0;
    let r1;
    let c0;
    let c1;
    if (kind === 'col') {
      const inSel = prev && index >= prev.c0 && index <= prev.c1
        && prev.r0 === 0 && prev.r1 === rows - 1;
      c0 = inSel ? prev.c0 : index;
      c1 = inSel ? prev.c1 : index;
      r0 = 0;
      r1 = rows - 1;
      if (!inSel) selectColumn(index, false);
    } else {
      const inSel = prev && index >= prev.r0 && index <= prev.r1
        && prev.c0 === 0 && prev.c1 === cols - 1;
      r0 = inSel ? prev.r0 : index;
      r1 = inSel ? prev.r1 : index;
      c0 = 0;
      c1 = cols - 1;
      if (!inSel) selectRow(index, false);
    }
    setCtxMenu({
      x: e.clientX,
      y: e.clientY,
      kind,
      r0,
      r1,
      c0,
      c1,
    });
  };

  const tryPointColsFromHeader = (c, e) => {
    const ed = editingRef.current;
    if (!ed || !String(ed.draft || '').startsWith('=')) return false;
    const caret = ed.caret != null ? ed.caret : ed.draft.length;
    if (!canPointInsert(ed.draft, caret) && !pointingRef.current) return false;
    skipBlurCommitRef.current = true;
    const anchor = { col: c };
    insertPointedColsAt(anchor.col, c);
    const onMove = (ev) => {
      const t = document.elementFromPoint(ev.clientX, ev.clientY);
      const th = t?.closest?.('[data-ss-col-h]');
      if (!th) return;
      const nc = Number(th.getAttribute('data-ss-col-h'));
      if (!Number.isFinite(nc)) return;
      insertPointedColsAt(anchor.col, nc);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.setTimeout(() => { skipBlurCommitRef.current = false; }, 0);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    e.preventDefault();
    return true;
  };

  const selectAll = () => {
    setRanges(replaceRanges({r0: 0, r1: rows - 1, c0: 0, c1: cols - 1 }));
    focusWorkspace();
  };

  const onColResizeMouseDown = (e, c) => {
    if (readOnly) return;
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = getColWidth(stateRef.current, c);
    const bounds = primaryRange(rangesRef.current);
    const targets = (bounds && c >= bounds.c0 && c <= bounds.c1)
      ? Array.from({ length: bounds.c1 - bounds.c0 + 1 }, (_, i) => bounds.c0 + i)
      : [c];
    const onMove = (ev) => {
      const next = startW + (ev.clientX - startX);
      setState((prev) => setColWidths(prev, targets, next));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      focusWorkspace();
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const renderCell = (r, c) => {
    const raw = rawCell(state, r, c);
    const shown = displayValue(state, r, c, evalCell);
    const isFormula = raw.startsWith('=');
    const isActive = active.row === r && active.col === c;
    const selected = isInRanges(ranges, r, c);
    const isEdit = editing && editing.row === r && editing.col === c;
    const fill = getFill(state, r, c);
    const walls = getWalls(state, r, c);
    const align = getAlign(state, r, c);
    const isFillHandle = b && r === b.r1 && c === b.c1 && isActive && !editing;
    const remoteEd = remoteEditorOnCell(remotes, r, c);
    const isLocalPoint = localPoint && (
      localPoint.kind === 'cols'
        ? (c >= Math.min(localPoint.c0 ?? localPoint.col, localPoint.c1 ?? localPoint.col)
          && c <= Math.max(localPoint.c0 ?? localPoint.col, localPoint.c1 ?? localPoint.col))
        : (localPoint.r0 != null)
          ? (r >= Math.min(localPoint.r0, localPoint.r1 ?? localPoint.r0)
            && r <= Math.max(localPoint.r0, localPoint.r1 ?? localPoint.r0)
            && c >= Math.min(localPoint.c0 ?? localPoint.col, localPoint.c1 ?? localPoint.col)
            && c <= Math.max(localPoint.c0 ?? localPoint.col, localPoint.c1 ?? localPoint.col))
          : (localPoint.row === r && localPoint.col === c)
    );
    const remotePoint = remotePointHighlights.find((p) => p.row === r && p.col === c && !p.soft);
    const remoteSoft = remotePointHighlights.find((p) => p.row === r && p.col === c && p.soft);
    const remoteSel = remoteRangesOnCell(remotes, r, c)[0];
    const refHl = (!isEdit && editing) ? refHlOnCell(r, c) : null;
    const refColor = refHl ? REF_HL_COLORS[refHl.colorIndex % REF_HL_COLORS.length] : null;
    const antsB = ants?.bounds;
    const isAnts = antsB
      && r >= antsB.r0 && r <= antsB.r1 && c >= antsB.c0 && c <= antsB.c1;
    const remoteAnts = remotes.find((rm) => {
      const a = rm.sheet?.ants;
      return a && r >= a.r0 && r <= a.r1 && c >= a.c0 && c <= a.c1;
    });

    let displayText = isFormula ? shown : raw;
    if (remoteEd && !isEdit) {
      displayText = remoteEd.sheet.editing.draft;
    }

    const boxShadow = [];
    if (remoteEd) boxShadow.push(`inset 0 0 0 2px ${remoteEd.color}`);
    if (isLocalPoint) boxShadow.push('inset 0 0 0 2px #fbbf24');
    if (remotePoint) boxShadow.push(`inset 0 0 0 2px ${remotePoint.color}`);
    if (refColor) boxShadow.push(`inset 0 0 0 2px ${refColor}`);
    if (remoteSel && !selected) {
      boxShadow.push(`inset 0 0 0 1px ${remoteSel.color}`);
    }

    return (
      <td
        key={c}
        data-ss-r={r}
        data-ss-c={c}
        className={[
          'ss-td',
          wallClassName(walls),
          isFormula ? 'is-formula' : '',
          selected ? 'is-selected' : '',
          isActive ? 'is-active' : '',
          remoteEd ? 'is-remote-edit' : '',
          isLocalPoint ? 'is-point-target' : '',
          remotePoint ? 'is-remote-point' : '',
          isAnts ? 'ss-marching-ants' : '',
          remoteAnts ? 'ss-marching-ants ss-marching-ants-remote' : '',
          refHl ? `ss-ref-hl ss-ref-hl-${refHl.colorIndex % 6}` : '',
          align === 'center' ? 'ss-align-center' : '',
          align === 'right' ? 'ss-align-right' : '',
        ].filter(Boolean).join(' ')}
        style={{
          background: fill || undefined,
          height: ROW_H,
          width: getColWidth(state, c),
          minWidth: getColWidth(state, c),
          maxWidth: getColWidth(state, c),
          textAlign: align || undefined,
          boxShadow: boxShadow.length ? boxShadow.join(', ') : undefined,
          ...(remoteAnts ? { '--ss-ants-color': remoteAnts.color } : null),
          ...(remoteSoft && !remoteEd && !isLocalPoint && !remotePoint && !refColor
            ? { outline: `1px dashed ${remoteSoft.color}`, outlineOffset: -1 }
            : null),
        }}
        onMouseDown={(ev) => onCellMouseDown(ev, r, c)}
        onDoubleClick={() => startEdit(r, c, undefined, { deep: true })}
      >
        {isEdit ? (
          <input
            ref={editInputRef}
            className="form-input ss-cell ss-cell-edit"
            value={editing.draft}
            onChange={onEditInputChange}
            onSelect={onEditSelect}
            onClick={onEditSelect}
            onBlur={onEditBlur}
            onKeyDown={onKeyDown}
            aria-label={`${colLabel(c)}${r + 1}`}
          />
        ) : (
          <div
            className={`ss-cell-display ${remoteEd ? 'is-live-draft' : ''}`}
            title={remoteEd ? `Набор: ${remoteEd.sheet.editing.draft}` : (isFormula ? raw : undefined)}
            style={remoteEd ? { color: remoteEd.color } : undefined}
          >
            {remoteEd ? remoteEd.sheet.editing.draft : displayText}
          </div>
        )}
        {isFillHandle && !readOnly ? (
          <span
            className="ss-fill-handle"
            onMouseDown={onFillHandleMouseDown}
            onDoubleClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              fillDownFromHandle();
            }}
            title="Протянуть; двойной клик — вниз до конца данных"
          />
        ) : null}
      </td>
    );
  };

  const formulaBarValue = editing
    ? editing.draft
    : (remoteEditorOnCell(remotes, active.row, active.col)?.sheet?.editing?.draft ?? formulaBar);

  const colHeaders = (
    <tr>
      <th
        className="ss-corner"
        title="Выделить всё"
        onMouseDown={(e) => {
          e.preventDefault();
          selectAll();
        }}
      />
      {Array.from({ length: cols }, (_, c) => (
        <th
          key={c}
          data-ss-col-h={c}
          className={`ss-col-h ${b && c >= b.c0 && c <= b.c1 && b.r0 === 0 && b.r1 === rows - 1 ? 'is-col-selected' : ''}`}
          style={{
            width: getColWidth(state, c),
            minWidth: getColWidth(state, c),
            maxWidth: getColWidth(state, c),
          }}
          onMouseDown={(e) => {
            if (e.target?.closest?.('.ss-col-resize')) return;
            if (e.button === 2) return;
            if (tryPointColsFromHeader(c, e)) return;
            e.preventDefault();
            if (editingRef.current) commitEdit();
            selectColumn(c, e.shiftKey);
          }}
          onContextMenu={(e) => openHeaderContextMenu(e, 'col', c)}
        >
          <span className="ss-col-h-label">{colLabel(c)}</span>
          {filterState.enabled ? (
            <button
              type="button"
              className={`ss-filter-btn ${filterState.columns[String(c)] ? 'has-filter' : ''}`}
              title="Фильтр столбца"
              onMouseDown={(ev) => {
                ev.preventDefault();
                ev.stopPropagation();
                setFilterPopupCol((prev) => (prev === c ? null : c));
              }}
            >
              ▾
            </button>
          ) : null}
          {!readOnly ? (
            <span
              className="ss-col-resize"
              onMouseDown={(ev) => onColResizeMouseDown(ev, c)}
              title="Ширина столбца"
            />
          ) : null}
        </th>
      ))}
    </tr>
  );

  const isTextFieldEvent = (e) => {
    const t = e.target;
    if (!t) return false;
    const tag = t.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || t.isContentEditable;
  };

  const armAntsFromSelection = useCallback(() => {
    const st = stateRef.current;
    const b = primaryRange(rangesRef.current);
    if (!b) return null;
    const origin = { row: activeRef.current.row, col: activeRef.current.col };
    // активная должна быть в выделении, иначе — угол
    const orow = isInRanges(rangesRef.current, origin.row, origin.col)
      ? origin.row
      : b.r0;
    const ocol = isInRanges(rangesRef.current, origin.row, origin.col)
      ? origin.col
      : b.c0;
    const value = st.cells?.[`${orow},${ocol}`] ?? '';
    const fill = st.fills?.[`${orow},${ocol}`] || null;
    const next = {
      origin: { row: orow, col: ocol },
      value,
      fill,
      bounds: { r0: b.r0, r1: b.r1, c0: b.c0, c1: b.c1 },
    };
    setAnts(next);
    antsRef.current = next;
    appendAction('copy-ants', next.bounds);
    return next;
  }, []);

  const onCopyCapture = (e) => {
    if (editingRef.current || isTextFieldEvent(e)) return;
    const primary = primaryRange(rangesRef.current);
    const payload = selectionToClipboard(stateRef.current, primary);
    if (!payload) return;
    e.preventDefault();
    internalClipboardRef.current = payload;
    e.clipboardData?.setData('text/plain', payload.tsv);
    armAntsFromSelection();
  };

  const onPasteCapture = (e) => {
    if (editingRef.current || readOnly || isTextFieldEvent(e)) return;
    e.preventDefault();
    const text = e.clipboardData?.getData('text/plain') ?? '';
    pasteAtActive(text);
    setAnts(null);
    antsRef.current = null;
  };

  const onCutCapture = (e) => {
    if (editingRef.current || readOnly || isTextFieldEvent(e)) return;
    const primary = primaryRange(rangesRef.current);
    const payload = selectionToClipboard(stateRef.current, primary);
    if (!payload) return;
    e.preventDefault();
    internalClipboardRef.current = payload;
    e.clipboardData?.setData('text/plain', payload.tsv);
    armAntsFromSelection();
    setState((prev) => {
      let next = prev;
      forEachCellInRanges(rangesRef.current, (r, c) => {
        next = clearSelectionCells(next, { r0: r, r1: r, c0: c, c1: c });
      });
      return next;
    });
    setFormulaBar('');
  };

  return (
    <div
      ref={workspaceRef}
      className={`spreadsheet-workspace ${virtual ? 'is-virtual' : ''}`}
      tabIndex={0}
      onKeyDown={onKeyDown}
      onCopy={onCopyCapture}
      onPaste={onPasteCapture}
      onCut={onCutCapture}
    >
      <div className="ss-toolbar gtw-toolbar">
        <label className="ss-formula-bar">
          <span className="ss-ref">{colLabel(active.col)}{active.row + 1}</span>
          <input
            className="form-input"
            value={formulaBarValue}
            disabled={readOnly}
            onChange={(e) => {
              const v = e.target.value;
              const caret = e.target.selectionStart ?? v.length;
              if (!editing) {
                startEdit(active.row, active.col, v, { caret, deep: true });
                return;
              }
              if (pointingRef.current) {
                setPointing(null);
                pointingRef.current = null;
              }
              const nextEd = { ...editingRef.current, draft: v, caret };
              setEditing(nextEd);
              editingRef.current = nextEd;
              setFormulaBar(v);
              publishAwareness(nextEd, null);
            }}
            onFocus={() => {
              if (!editing && !readOnly) {
                startEdit(active.row, active.col, undefined, { deep: true });
              }
            }}
            onDoubleClick={(e) => {
              if (readOnly) return;
              e.preventDefault();
              startEdit(active.row, active.col, undefined, { deep: true });
              requestAnimationFrame(() => {
                const el = e.currentTarget;
                if (el && typeof el.setSelectionRange === 'function') {
                  const len = String(el.value || '').length;
                  el.focus();
                  el.setSelectionRange(len, len);
                }
              });
            }}
            onBlur={() => {
              if (skipBlurCommitRef.current) return;
              if (editingRef.current) commitEdit();
            }}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') {
                e.preventDefault();
                commitEdit();
                e.currentTarget.blur();
                return;
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                cancelEdit();
                e.currentTarget.blur();
                return;
              }
              // pointing из строки формул
              const ed = editingRef.current;
              if (!ed) return;
              const caret = e.currentTarget.selectionStart ?? ed.draft.length;
              const arrowMap = {
                ArrowUp: [-1, 0],
                ArrowDown: [1, 0],
                ArrowLeft: [0, -1],
                ArrowRight: [0, 1],
              };
              if (arrowMap[e.key] && String(ed.draft).startsWith('=')) {
                if (canPointInsert(ed.draft, caret) || pointingRef.current) {
                  e.preventDefault();
                  const [dr, dc] = arrowMap[e.key];
                  const base = pointingRef.current || { row: ed.row, col: ed.col };
                  const next = movePoint(base.row, base.col, dr, dc, rows, cols);
                  insertPointedCell(next.row, next.col);
                }
              }
            }}
            placeholder="=СУММ(A1:A5)"
            aria-label="Строка формул"
          />
        </label>
        <span className="ss-fill-swatches" role="group" aria-label="Цвет заливки">
          {FILL_PRESETS.map((p, i) => {
            const prev = FILL_PRESETS[i - 1];
            const showSep = i > 0 && p.group && prev?.group && p.group !== prev.group;
            return (
              <span key={p.id} className="ss-fill-swatch-wrap">
                {showSep ? <span className="ss-fill-sep" aria-hidden="true" /> : null}
                <button
                  type="button"
                  className={`ss-fill-swatch ${fillColor === p.color ? 'is-active-tool' : ''} ${!p.color ? 'is-none' : ''}`}
                  style={p.color ? { background: p.color } : undefined}
                  title={p.label}
                  disabled={readOnly}
                  aria-label={p.label}
                  onClick={() => {
                    setFillColor(p.color);
                    if (!b || readOnly) return;
                    applyToAllRanges((prevState, rng) => applyFillToSelection(prevState, rng, p.color));
                  }}
                />
              </span>
            );
          })}
        </span>
        <span className="ss-align-btns" role="group" aria-label="Выравнивание">
          {[
            { id: 'left', title: 'По левому краю', label: '≡◁' },
            { id: 'center', title: 'По центру', label: '≡' },
            { id: 'right', title: 'По правому краю', label: '▷≡' },
          ].map((a) => (
            <button
              key={a.id}
              type="button"
              className="btn btn-sm ss-align-btn"
              disabled={readOnly || !b}
              title={a.title}
              onClick={() => applyToAllRanges((prev, rng) => applyAlignToSelection(prev, rng, a.id))}
            >
              {a.label}
            </button>
          ))}
        </span>
        <button
          type="button"
          className={`btn btn-sm ${filterState.enabled ? 'is-active-tool' : ''}`}
          disabled={readOnly}
          title="Автофильтр"
          onClick={() => setState((prev) => setFilterEnabled(prev, !normalizeFilterState(prev.filter).enabled))}
        >
          Фильтр
        </button>
        <span className="ss-wall-sides" role="group" aria-label="Стороны стен">
          {WALL_SIDE_BTNS.map(({ key, title }) => (
            <button
              key={key}
              type="button"
              className={`btn btn-sm ss-wall-side ${wallSides[key] ? 'is-active-tool' : ''}`}
              disabled={readOnly}
              title={title}
              aria-pressed={wallSides[key]}
              onClick={() => setWallSides((prev) => ({ ...prev, [key]: !prev[key] }))}
            >
              <WallSideIcon side={key} />
            </button>
          ))}
        </span>
        <button
          type="button"
          className="btn btn-sm"
          disabled={readOnly || !b || !(wallSides.t || wallSides.r || wallSides.b || wallSides.l)}
          onClick={() => applyToAllRanges((prev, rng) => applyWallsToSelection(prev, rng, {
            t: wallSides.t ? 1 : 0,
            r: wallSides.r ? 1 : 0,
            b: wallSides.b ? 1 : 0,
            l: wallSides.l ? 1 : 0,
          }))}
          title="Поставить стены на выбранных сторонах периметра"
        >
          Стены
        </button>
        <button
          type="button"
          className="btn btn-sm"
          disabled={readOnly || !b || !(wallSides.t || wallSides.r || wallSides.b || wallSides.l)}
          onClick={() => applyToAllRanges((prev, rng) => clearWallsInSelection(prev, rng, {
            t: wallSides.t ? 1 : 0,
            r: wallSides.r ? 1 : 0,
            b: wallSides.b ? 1 : 0,
            l: wallSides.l ? 1 : 0,
          }))}
          title="Снять стены на выбранных сторонах периметра"
        >
          Снять
        </button>
        <button
          type="button"
          className={`btn btn-sm ${paintMode ? 'is-active-tool' : ''}`}
          disabled={readOnly}
          onClick={() => setPaintMode((v) => !v)}
          title="Скопировать формат активной ячейки на следующую"
        >
          Формат
        </button>
        {taskSlug ? (
          <button
            type="button"
            className="btn btn-sm"
            disabled={readOnly || sheetLoading}
            onClick={() => {
              if (!isSheetEmpty(stateRef.current)
                && !window.confirm('Заменить текущий лист данными из файла задачи?')) {
                return;
              }
              void loadFromTaskFile({ force: false });
            }}
            title="Загрузить .xlsx / .ods из вложений задачи"
          >
            {sheetLoading ? 'Загрузка…' : 'Из файла'}
          </button>
        ) : null}
      </div>
      <p className="ss-hint">
        {virtual
          ? `Лист ${rows}×${cols}, видно окно строк. `
          : `Лист ${rows}×${cols}. `}
        {sheetSource ? `Файл: ${sheetSource}. ` : null}
        {lockHint ? <span className="ss-sheet-error">{lockHint} </span> : null}
        {sheetError ? <span className="ss-sheet-error">{sheetError} </span> : null}
        F2 / «=» — формула; в формуле стрелки, клик/протяжка или заголовки столбцов — ссылка/A:C.
        ПКМ по заголовку — вставить/удалить. Двойной клик по маркеру — вниз.
        Enter — записать, Esc — отмена. Ctrl+A — всё.
      </p>
      <div className="ss-scroll" ref={scrollRef}>
        <table className="ss-table">
          <colgroup>
            <col className="ss-row-h-col" />
            {Array.from({ length: cols }, (_, c) => (
              <col key={c} style={{ width: getColWidth(state, c) }} />
            ))}
          </colgroup>
          <thead>{colHeaders}</thead>
          <tbody>
            {virtual && topPad > 0 ? (
              <tr aria-hidden className="ss-pad-row">
                <td colSpan={cols + 1} style={{ height: topPad, padding: 0, border: 'none' }} />
              </tr>
            ) : null}
            {(filterState.enabled
              ? visibleRows
              : Array.from({ length: rowEnd - rowStart + 1 }, (_, i) => rowStart + i)
            ).map((r) => (
              <tr key={r} style={{ height: ROW_H }}>
                <th
                  className={`ss-row-h ${b && r >= b.r0 && r <= b.r1 && b.c0 === 0 && b.c1 === cols - 1 ? 'is-row-selected' : ''}`}
                  onMouseDown={(e) => {
                    if (e.button === 2) return;
                    e.preventDefault();
                    if (editingRef.current) commitEdit();
                    selectRow(r, e.shiftKey);
                  }}
                  onContextMenu={(e) => openHeaderContextMenu(e, 'row', r)}
                >
                  {r + 1}
                </th>
                {Array.from({ length: cols }, (_, c) => renderCell(r, c))}
              </tr>
            ))}
            {virtual && bottomPad > 0 ? (
              <tr aria-hidden className="ss-pad-row">
                <td colSpan={cols + 1} style={{ height: bottomPad, padding: 0, border: 'none' }} />
              </tr>
            ) : null}
          </tbody>
        </table>
        {filterPopupCol != null ? (
          <FilterPopup
            col={filterPopupCol}
            state={state}
            evalCell={evalCell}
            readOnly={readOnly}
            onClose={() => setFilterPopupCol(null)}
            onChange={(patch) => setState((prev) => setColumnFilter(prev, filterPopupCol, patch))}
            onClear={() => {
              setState((prev) => clearColumnFilter(prev, filterPopupCol));
              setFilterPopupCol(null);
            }}
          />
        ) : null}
      </div>
      {(pressedKeys.length > 0 || remotes.some((r) => r.sheet?.keys?.length)) ? (
        <div className="ss-keys-overlay" aria-live="polite">
          {pressedKeys.length > 0 ? (
            <div className="ss-keys-local">
              {pressedKeys.map((k) => (
                <kbd key={`l-${k}`}>{k}</kbd>
              ))}
            </div>
          ) : null}
          {remotes.map((r) => (
            r.sheet?.keys?.length ? (
              <div key={r.clientId} className="ss-keys-remote" style={{ borderColor: r.color }}>
                <span className="ss-keys-name" style={{ color: r.color }}>
                  {r.name || 'Участник'}
                </span>
                {r.sheet.keys.map((k) => (
                  <kbd key={`${r.clientId}-${k}`}>{k}</kbd>
                ))}
              </div>
            ) : null
          ))}
        </div>
      ) : null}
      {ctxMenu ? (
        <HeaderContextMenu
          menu={ctxMenu}
          onClose={() => setCtxMenu(null)}
          onInsert={() => {
            const m = ctxMenu;
            setCtxMenu(null);
            if (!m || readOnly) return;
            if (m.kind === 'col') {
              const count = m.c1 - m.c0 + 1;
              setState((prev) => insertCols(prev, m.c0, count));
              setActive({ row: activeRef.current.row, col: m.c0 });
              setRanges(replaceRanges({
                r0: 0, r1: rows - 1, c0: m.c0, c1: m.c0 + count - 1,
              }));
            } else {
              const count = m.r1 - m.r0 + 1;
              setState((prev) => insertRows(prev, m.r0, count));
              setActive({ row: m.r0, col: activeRef.current.col });
              setRanges(replaceRanges({
                r0: m.r0, r1: m.r0 + count - 1, c0: 0, c1: cols - 1,
              }));
            }
            focusWorkspace();
          }}
          onDelete={() => {
            const m = ctxMenu;
            setCtxMenu(null);
            if (!m || readOnly) return;
            if (m.kind === 'col') {
              setState((prev) => deleteCols(prev, m.c0, m.c1));
              const nc = Math.min(m.c0, Math.max(0, cols - (m.c1 - m.c0 + 1) - 1));
              setActive({ row: activeRef.current.row, col: Math.max(0, nc) });
              setRanges(replaceRanges({
                r0: 0, r1: rows - 1, c0: Math.max(0, nc), c1: Math.max(0, nc),
              }));
            } else {
              setState((prev) => deleteRows(prev, m.r0, m.r1));
              const nr = Math.min(m.r0, Math.max(0, rows - (m.r1 - m.r0 + 1) - 1));
              setActive({ row: Math.max(0, nr), col: activeRef.current.col });
              setRanges(replaceRanges({
                r0: Math.max(0, nr), r1: Math.max(0, nr), c0: 0, c1: cols - 1,
              }));
            }
            focusWorkspace();
          }}
        />
      ) : null}
    </div>
  );
}

function HeaderContextMenu({ menu, onClose, onInsert, onDelete }) {
  useEffect(() => {
    const close = () => onClose();
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('mousedown', close);
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);
    return () => {
      window.removeEventListener('mousedown', close);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
    };
  }, [onClose]);

  const isCol = menu.kind === 'col';
  const count = isCol ? (menu.c1 - menu.c0 + 1) : (menu.r1 - menu.r0 + 1);
  const insertLabel = isCol
    ? (count > 1 ? `Вставить ${count} столбца(ов) слева` : 'Вставить столбец слева')
    : (count > 1 ? `Вставить ${count} строки(ок) выше` : 'Вставить строку выше');
  const deleteLabel = isCol
    ? (count > 1 ? `Удалить ${count} столбца(ов)` : 'Удалить столбец')
    : (count > 1 ? `Удалить ${count} строки(ок)` : 'Удалить строку');

  return (
    <div
      className="ss-ctx-menu"
      style={{ left: menu.x, top: menu.y }}
      role="menu"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <button type="button" role="menuitem" className="ss-ctx-item" onClick={onInsert}>
        {insertLabel}
      </button>
      <button type="button" role="menuitem" className="ss-ctx-item" onClick={onDelete}>
        {deleteLabel}
      </button>
    </div>
  );
}

function FilterPopup({
  col, state, evalCell, readOnly, onClose, onChange, onClear,
}) {
  const conf = normalizeFilterState(state.filter).columns[String(col)] || {
    sort: null, selected: null, condition: null,
  };
  const values = uniqueColumnValues(state, col, evalCell);
  const selected = conf.selected == null ? new Set(values) : new Set(conf.selected);
  const [condOp, setCondOp] = useState(conf.condition?.op || 'eq');
  const [condVal, setCondVal] = useState(conf.condition?.value || '');

  const toggleVal = (v) => {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    onChange({
      selected: next.size === values.length ? null : Array.from(next),
    });
  };

  return (
    <div className="ss-filter-popup" role="dialog" aria-label={`Фильтр ${colLabel(col)}`}>
      <div className="ss-filter-popup-head">
        <strong>{colLabel(col)}</strong>
        <button type="button" className="btn btn-xs" onClick={onClose}>✕</button>
      </div>
      <div className="ss-filter-sort">
        <button
          type="button"
          className={`btn btn-sm ${conf.sort === 'asc' ? 'is-active-tool' : ''}`}
          disabled={readOnly}
          onClick={() => onChange({ sort: conf.sort === 'asc' ? null : 'asc' })}
        >
          А → Я
        </button>
        <button
          type="button"
          className={`btn btn-sm ${conf.sort === 'desc' ? 'is-active-tool' : ''}`}
          disabled={readOnly}
          onClick={() => onChange({ sort: conf.sort === 'desc' ? null : 'desc' })}
        >
          Я → А
        </button>
      </div>
      <div className="ss-filter-values">
        <label className="ss-filter-check">
          <input
            type="checkbox"
            checked={selected.size === values.length}
            disabled={readOnly}
            onChange={() => onChange({
              selected: selected.size === values.length ? [] : null,
            })}
          />
          Выделить всё
        </label>
        {values.map((v) => (
          <label key={v || '__empty'} className="ss-filter-check">
            <input
              type="checkbox"
              checked={selected.has(v)}
              disabled={readOnly}
              onChange={() => toggleVal(v)}
            />
            <span>{v === '' ? '(пустые)' : v}</span>
          </label>
        ))}
      </div>
      <div className="ss-filter-cond">
        <select
          className="form-input"
          value={condOp}
          disabled={readOnly}
          onChange={(e) => setCondOp(e.target.value)}
        >
          {FILTER_OPS.map((op) => (
            <option key={op.id} value={op.id}>{op.label}</option>
          ))}
        </select>
        <input
          className="form-input"
          value={condVal}
          disabled={readOnly}
          placeholder="Значение"
          onChange={(e) => setCondVal(e.target.value)}
        />
        <button
          type="button"
          className="btn btn-sm"
          disabled={readOnly}
          onClick={() => onChange({
            condition: condVal === '' && condOp !== 'eq'
              ? conf.condition
              : { op: condOp, value: condVal },
          })}
        >
          Условие
        </button>
      </div>
      <div className="ss-filter-actions">
        <button type="button" className="btn btn-sm" disabled={readOnly} onClick={onClear}>
          Сбросить
        </button>
        <button type="button" className="btn btn-sm" onClick={onClose}>
          OK
        </button>
      </div>
    </div>
  );
}
